import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { router } from 'expo-router';
import { Plus, TriangleAlert } from 'lucide-react-native';
import { categoryLabels, colors, pick, PRODUCT_CATEGORIES, statusLabels, type ApiProduct, type ProductCategory } from '@hogar/shared';
import { Button, Card, RowLink, Screen, SectionTitle, Txt, useT } from '../../components/ui';
import { categoryIcons, productIcons } from '../../components/icons';
import { StatusSheet, statusColor, statusTextColor } from '../../components/StatusSheet';
import { byUrgency, toRestock, useHouse } from '../../lib/house';
import { crewSlice, useCrew } from '../../lib/crew';
import { CrewSwitch } from '../../components/CrewSwitch';

const SOFT = { good: colors.primarySoft, low: colors.warningSoft, out: colors.dangerSoft } as const;

export default function Products() {
  const { t, lang } = useT();
  const crew = useCrew();
  const { products } = crewSlice(useHouse(), crew);
  // The garden's supplies have their own category; the house's use the rest.
  const categories = PRODUCT_CATEGORIES.filter((c) => (crew === 'garden') === (c === 'garden'));
  const [showAll, setShowAll] = useState(false);
  const [category, setCategory] = useState<ProductCategory | null>(null);
  const [editing, setEditing] = useState<ApiProduct | null>(null);
  const restock = toRestock(products);
  const list = category ? byUrgency(products.filter((p) => p.category === category)) : showAll || restock.length === 0 ? byUrgency(products) : restock;

  return (
    <Screen title={t('products')}>
      <CrewSwitch />
      {restock.length > 0 && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: colors.alertBanner, borderRadius: 14, padding: 14, marginBottom: 12 }}>
          <TriangleAlert size={18} color={colors.warning} />
          <Txt weight="medium" size={15}>
            {restock.length === 1 ? t('toRestockOne') : t('toRestock', { n: restock.length })}
          </Txt>
        </View>
      )}

      {category && (
        <Txt weight="semibold" size={16} style={{ marginBottom: 8 }}>
          {categoryLabels[category][lang]}
        </Txt>
      )}

      {list.length > 0 && (
        <Card>
          {list.map((p, i) => (
            <RowLink
              key={p.id}
              Icon={productIcons[p.icon]}
              iconTint={statusColor[p.status]}
              iconBg={SOFT[p.status]}
              round
              title={pick(p.name, lang)}
              subtitle={statusLabels[p.status][lang]}
              subtitleColor={p.status === 'good' ? colors.textMuted : statusTextColor[p.status]}
              onPress={() => setEditing(p)}
              last={i === list.length - 1}
            />
          ))}
        </Card>
      )}

      {category ? (
        <Button label={t('allCategories')} variant="outline" onPress={() => setCategory(null)} />
      ) : restock.length > 0 ? (
        <Button label={showAll ? t('showLess') : t('seeFullList')} variant="outline" onPress={() => setShowAll(!showAll)} />
      ) : null}
      <Button label={t('addProduct')} Icon={Plus} variant="outline" onPress={() => router.push({ pathname: '/product-edit', params: { crew, ...(category ? { category } : crew === 'garden' ? { category: 'garden' } : {}) } })} style={{ marginTop: 10 }} />

      <SectionTitle>{t('byCategory')}</SectionTitle>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
        {categories.map((c) => {
          const Icon = categoryIcons[c];
          const active = c === category;
          return (
            <Pressable
              key={c}
              onPress={() => setCategory(active ? null : c)}
              style={{ width: '31%', flexGrow: 1, minHeight: 92, alignItems: 'center', justifyContent: 'center', gap: 8, padding: 8, borderRadius: 16, backgroundColor: active ? colors.primarySoft : colors.card, borderWidth: 1, borderColor: active ? colors.primary : colors.cardBorder }}
            >
              <Icon size={24} color={colors.text} strokeWidth={1.5} />
              <Txt size={12} weight="medium" style={{ textAlign: 'center' }}>
                {categoryLabels[c][lang]}
              </Txt>
            </Pressable>
          );
        })}
      </View>

      <StatusSheet
        product={editing}
        onClose={() => setEditing(null)}
        onEdit={(p) => {
          setEditing(null);
          router.push({ pathname: '/product-edit', params: { id: p.id } });
        }}
      />
    </Screen>
  );
}
